import PropTypes from 'prop-types';
import FormRow from '@/components/core/form/FormRow';
import InputBase from '@/components/core/form/InputBase';
import LocationBase from '@/components/core/form/LocationBase';
import { isDefined } from '@/utils/miscUtils';
import { useDerivedParam } from '@/stores/derivedCacheStore';
import { useDerivedValueFetch } from '@/hooks/useDerivedValueFetch';
import usePatchParams from '../../usePatchParams';
import { useParamsContext } from '@/components/providers/useParamsContext';
import ErrorMessage from '../ErrorMessage';

const DerivedLocationField = ({ param, paramSchema, name }) => {
  const { label, tooltip, source, editable, allow_null } = paramSchema;
  const { primary: { value, is_default: isDefault } = {} } = param ?? {};
  const { fetch, changed_dependencies: changedDependencies } = value ?? {};

  const { readOnly } = useParamsContext();
  const { patchPrimary } = usePatchParams();
  const results = useDerivedParam(name);
  const shouldFetch = fetch || !results;

  const { isLoading } = useDerivedValueFetch({ name, shouldFetch, changedDependencies });

  if (!isDefined(results) && !isLoading) {
    return null;
  }

  const isEditable = editable && !readOnly;

  const handlePlaceChanged = async ({ lat, lon, address }) => {
    await patchPrimary(name, { lat, lon, address });
  };

  const handleBlur = async ({ lat, lon, address }) => {
    await patchPrimary(name, { lat, lon, address });
  };

  return (
    <FormRow label={label} tooltip={tooltip} source={source} name={name}>
      <div className="flex flex-col flex-1">
        {isLoading ? (
          <InputBase name={name} readOnly placeholder="Loading..." isDefault={isDefault} />
        ) : isEditable ? (
          <LocationBase
            name={name}
            value={results}
            isDefault={isDefault}
            onPlaceChanged={handlePlaceChanged}
            onBlur={handleBlur}
            shouldValidate={!allow_null}
          />
        ) : (
          <>
            <div className="relative flex">
              <InputBase
                id={name}
                name={name}
                type="text"
                defaultValue={results?.address}
                readOnly
                isDefault={isDefault}
                className="pr-8 w-full"
              />
            </div>
            {results?.lat && results?.lon && (
              <div className="text-xs text-gray-500 mt-1">
                lat: {results.lat}, lon: {results.lon}
              </div>
            )}
          </>
        )}
        <ErrorMessage />
      </div>
    </FormRow>
  );
};

DerivedLocationField.propTypes = {
  param: PropTypes.object,
  paramSchema: PropTypes.object,
  name: PropTypes.string,
};

export default DerivedLocationField;
